/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { apiCall } from 'app/shared/lib/api/api';
import { AuthContext } from 'app/um/lib/auth-context';
import { DateComparison } from 'app/shared/lib/date-utilities';
import { isNullOrUndefined, safe } from 'app/shared/lib/utilities';
import { getProviderContractStatus } from 'app/um/lib/utilities';
import * as UM from 'app/um/models/essette-api';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class AuthPageContext extends AuthContext {

    ////****************** Fields and Properties **************************************////

    public timestamp: Date | null = null;

    public customFieldTarget: Core.CustomFieldTarget | null = null;

    ////****************** Lifecycle ***********************************************////

    ////****************** Functions ***********************************************////
    public async refreshAuthSettings(auth?: UM.Auth): Promise<void> {
        await super.refreshAuthSettings(auth);

        if(!this.auth.id) return;

        await this.authDataComplete(this.auth.id);
    }

    protected async authDataComplete(authId: string): Promise<boolean> {
        if(this.authRequiredForDataEntryMet && safe(() => this.auth!.status!.flag!.isInitial)) {
            await this.dataEntryComplete(authId);
            this.auth = await this.loadAuth(authId);

            // Get Provider Contract Statuses after data entry completetion and auth reload
            getProviderContractStatus(this.auth, this.auth.provider?.attending);
            getProviderContractStatus(this.auth, this.auth.provider?.requested);
            getProviderContractStatus(this.auth, this.auth.provider?.requesting);
            getProviderContractStatus(this.auth, this.auth.provider?.servicing);

            return Promise.resolve(true);
        }
        return Promise.resolve(false);
    }

    protected checkRequiredForDataEntry(auth?: UM.Auth): string[] {
        const missingFields: string[] = [];

        if(!this.isConfigured) {
            throw new Error('Auth context not configured');
        }
        if(!auth) {
            auth = this.auth;
            if(!auth) throw new Error('missing auth object');
        }

        // separate out services from dental services
        const services = auth?.services?.filter(service => this.filterServicesOnDental(service, false)) || [];
        const dentalServices = auth?.services?.filter(service => this.filterServicesOnDental(service, true)) || [];

        this.settings.services.reqNotMet = false;
        this.settings.servicesStatus.reqNotMet = false;
        if(this.settings.services.reqDataEntry) {
            if(!services.length) {
                this.settings.services.reqNotMet = true;
                missingFields.push('um:service.$_plural');
            }
        }

        this.settings.servicesAuthDate.reqNotMet = false;
        if(this.settings.servicesAuthDate.reqDataEntry || this.settings.servicesExpDate.reqDataEntry) {
            let missingField: boolean = false;

            services.forEach(service => {
                const authorizedDate = service.authorizedDate || null;
                const expirationDate = service.expirationDate || null;
                const serviceValidStart = service.definition?.dateRange?.start || null;
                const serviceValidEnd = service.definition?.dateRange?.end || null;
                let allowNullDateOfService: boolean = service.status?.flag?.allowNullDateOfService || false;

                if(!service.definition?.id) { missingField = true; return; }

                //todo: consider refactoring this out now that we use a status flag to null dates of service (ess-1610)
                if(service.status?.determination?.status?.determination?.result) {
                    //if determination is void, we don't care about dates for that item
                    switch(service.status?.determination?.status?.determination?.result) {
                        case 'other':
                            return;
                    }
                }

                //make sure each field that is required is filled in
                if(!authorizedDate && this.settings.servicesAuthDate.reqDataEntry && !allowNullDateOfService) { missingField = true; return; }
                if(!expirationDate && this.settings.servicesAuthDate.reqDataEntry && !allowNullDateOfService) { missingField = true; return; }

                //validate that the dates selected aren't outside the service valid dates
                if(!DateComparison.checkForContainingDates(serviceValidStart, serviceValidEnd,
                    authorizedDate || serviceValidStart, expirationDate || serviceValidEnd)) {
                    missingField = true;
                    return;
                }
            });

            if(missingField) {
                this.settings.servicesAuthDate.reqNotMet = true;
                missingFields.push('um:service.status.date.$_plural');
            }
            // Tim Monh TODO: Add check for overlapping dates if configured to do so. Check history of file to retrieve overlapping date check logic.
        }

        this.settings.servicesStatusReason.reqNotMet = false;
        if(this.settings.servicesStatusReason.reqDataEntry) {
            let missingField: boolean = false;

            services.forEach(service => {
                if(!service.definition?.id) { missingField = true; return; }

                //make sure each field that is required is filled in
                const statusReason = service.status?.reason || null;
                if(!statusReason && !!service.status?.reasons?.length) { missingField = true; return; }
            });

            if(missingField) {
                this.settings.servicesStatusReason.reqNotMet = true;
                missingFields.push('um:service.status.reason.$long_plural');
            }
            // Tim Monh TODO: Add check for overlapping dates if configured to do so. Check history of file to retrieve overlapping date check logic.
        }

        this.settings.servicesDental.reqNotMet = false;
        this.settings.servicesDentalStatus.reqNotMet = false;
        if(this.settings.servicesDental.reqDataEntry) {
            if(!dentalServices.length) {
                this.settings.servicesDental.reqNotMet = true;
                missingFields.push('um:service.dental.$long_plural');
            }
        }

        this.settings.servicesDentalAuthDate.reqNotMet = false;
        if(this.settings.servicesDentalAuthDate.reqDataEntry || this.settings.servicesDentalExpDate.reqDataEntry) {
            let missingField: boolean = false;
            dentalServices.forEach(service => {
                const authorizedDate = service.authorizedDate || null;
                const expirationDate = service.expirationDate || null;
                const serviceValidStart = service.definition?.dateRange?.start || null;
                const serviceValidEnd = service.definition?.dateRange?.end || null;
                let allowNullDateOfService: boolean = service.status?.flag?.allowNullDateOfService || false;

                if(!service.definition?.id) { missingField = true; return; }

                //todo: consider refactoring this out now that we use a status flag to null dates of service (ess-1610)
                if(service.status?.determination?.status?.determination?.result) {
                    //if determination is void, we don't care about dates for that item
                    switch(service.status?.determination?.status?.determination?.result) {
                        case 'other':
                            return;
                    }
                }

                //make sure each field that is required is filled in
                if(!authorizedDate && this.settings.servicesDentalAuthDate.reqDataEntry && !allowNullDateOfService) { missingField = true; return; }
                if(!expirationDate && this.settings.servicesDentalAuthDate.reqDataEntry && !allowNullDateOfService) { missingField = true; return; }

                //validate that the dates selected aren't outside the service valid dates
                if(!DateComparison.checkForContainingDates(serviceValidStart, serviceValidEnd,
                    authorizedDate || serviceValidStart, expirationDate || serviceValidEnd)) {
                    missingField = true;
                    return;
                }
            });

            if(missingField) {
                this.settings.servicesDentalAuthDate.reqNotMet = true;
                missingFields.push('um:service.dental.status.date.$_plural');
            }
            // Tim Monh TODO: Add check for overlapping dates if configured to do so. Check history of file to retrieve overlapping date check logic.
        }

        this.settings.servicesDentalStatusReason.reqNotMet = false;
        if(this.settings.servicesDentalStatusReason.reqDataEntry) {
            let missingField: boolean = false;

            dentalServices.forEach(service => {
                if(!service.definition?.id) { missingField = true; return; }

                //make sure each field that is required is filled in
                const statusReason = service.status?.reason || null;
                if(!statusReason && !!service.status?.reasons?.length) { missingField = true; return; }
            });

            if(missingField) {
                this.settings.servicesDentalStatusReason.reqNotMet = true;
                missingFields.push('um:service.dental.status.reason.$long_plural');
            }
            // Tim Monh TODO: Add check for overlapping dates if configured to do so. Check history of file to retrieve overlapping date check logic.
        }

        this.settings.chiefComplaint.reqNotMet = false;
        if(this.settings.chiefComplaint.reqDataEntry) {
            if(!auth.chiefComplaint) {
                this.settings.chiefComplaint.reqNotMet = true;
                missingFields.push('um:auth.chiefComplaint.$');
            }
        }

        this.settings.diagnoses.reqNotMet = false;
        if(this.settings.diagnoses.reqDataEntry) {
            if(!auth.diagnoses || !auth.diagnoses.length) {
                this.settings.diagnoses.reqNotMet = true;
                missingFields.push('um:auth.diagnosis.$_plural');
            }
        }

        this.settings.drugs.reqNotMet = false;
        this.settings.drugsStatus.reqNotMet = false;
        if(this.settings.drugs.reqDataEntry) {
            if(!auth.medications || !auth.medications.length) {
                this.settings.drugs.reqNotMet = true;
                missingFields.push('cd:drug.$_plural');
            }
        }

        this.settings.drugsDate.reqNotMet = false;
        if(this.settings.drugsAuthDate.reqDataEntry || this.settings.drugsExpDate.reqDataEntry) {
            let missingField: boolean = false;

            this.auth.medications?.forEach(medication => {
                const authorizedDate = medication.authorizedDate || null;
                const expirationDate = medication.expirationDate || null;
                const drugValidStart = medication.definition?.dateRange?.start || null;
                const drugValidEnd = medication.definition?.dateRange?.end || null;
                let allowNullDateOfService: boolean = medication.status?.flag?.allowNullDateOfService || false;

                if(!medication.definition?.id) { missingField = true; return; }

                //todo: consider refactoring this out now that we use a status flag to null dates of service (ess-1610)
                if(medication.status?.determination?.status?.determination?.result) {
                    //if determination is void, we don't care about dates for that item
                    switch(medication.status.determination?.status.determination.result) {
                        case 'other':
                            return;
                    }
                }

                //make sure each field that is required is filled in
                if(!authorizedDate && this.settings.drugsAuthDate.reqDataEntry && !allowNullDateOfService) { missingField = true; return; }
                if(!expirationDate && this.settings.drugsExpDate.reqDataEntry && !allowNullDateOfService) { missingField = true; return; }

                //validate that the dates selected aren't outside the drug valid dates
                if(!DateComparison.checkForContainingDates(drugValidStart, drugValidEnd,
                    authorizedDate || drugValidStart, expirationDate || drugValidEnd)) {
                    missingField = true;
                    return;
                }
            });

            if(missingField) {
                this.settings.drugsDate.reqNotMet = true;
                missingFields.push('um:medication.status.date.$_plural');
            }
            // Tim Monh TODO: Add check for overlapping dates if configured to do so. Check history of file to retrieve overlapping date check logic.
        }

        this.settings.drugsStatusReason.reqNotMet = false;
        if(this.settings.drugsStatusReason.reqDataEntry) {
            let missingField: boolean = false;

            this.auth.medications?.forEach(medication => {
                if(!medication.definition?.id) { missingField = true; return; }

                //make sure each field that is required is filled in
                const statusReason = medication.status?.reason || null;
                if(!statusReason && !!medication.status?.reasons?.length) { missingField = true; return; }
            });

            if(missingField) {
                this.settings.drugsStatusReason.reqNotMet = true;
                missingFields.push('um:medication.status.reason.$long_plural');
            }
            // Tim Monh TODO: Add check for overlapping dates if configured to do so. Check history of file to retrieve overlapping date check logic.
        }

        if(this.settings.priority.reqDataEntry) {
            if(!auth.priority) {
                missingFields.push('um:auth.priority.$');
            }
        }

        if(this.settings.providerAttending.reqDataEntry) {
            if(!auth.provider || !auth.provider.attending) {
                missingFields.push(this.settings.providerAttending.display!);
            }
        }

        if(this.settings.providerRequested.reqDataEntry) {
            if(!auth.provider || !auth.provider.requested) {
                missingFields.push(this.settings.providerRequested.display!);
            }
        }

        if(this.settings.providerRequesting.reqDataEntry) {
            if(!auth.provider || !auth.provider.requesting) {
                missingFields.push(this.settings.providerRequesting.display!);
            }
        }

        if(this.settings.providerServicing.reqDataEntry) {
            if(!auth.provider || !auth.provider.servicing) {
                missingFields.push(this.settings.providerServicing.display!);
            }
        }

        if(this.settings.requestedDateTime.reqDataEntry) {
            if(!auth.requestedDateTime) {
                missingFields.push('um:auth.requestedDate.$');
            }
        }

        if(this.settings.admissionSource.reqDataEntry) {
            if(!auth.admission?.source) {
                missingFields.push('um:auth.admission.source.$long');
            }
        }

        if(this.sourceOfAuthReqDataEntry) {
            if(!auth.source) {
                missingFields.push('um:auth.source.$long');
            }
        }

        if(this.transportationReqDataEntry) {
            if(!auth.transportation) {
                missingFields.push('um:auth.transportationAppointment.$long');
            }
        }

        this.settings.serviceRequestedCharges.reqNotMet = false;
        if(this.settings.serviceRequestedCharges.reqDataEntry) {
            if(services.find(s => !s.charge?.requested)) {
                this.settings.serviceRequestedCharges.reqNotMet = true;
                missingFields.push('um:service.charge.requested.$long_plural');
            }
        }

        this.settings.serviceApprovedCharges.reqNotMet = false;
        if(this.settings.serviceApprovedCharges.reqDataEntry) {
            if(services.find(s => !s.charge?.approved)) {
                this.settings.serviceApprovedCharges.reqNotMet = true;
                missingFields.push('um:service.charge.approved.$long_plural');
            }
        }

        this.settings.drugRequestedCharges.reqNotMet = false;
        if(this.settings.drugRequestedCharges.reqDataEntry) {
            if(auth.medications && auth.medications.find(m => !m.charge?.requested)) {
                this.settings.drugRequestedCharges.reqNotMet = true;
                missingFields.push('um:medication.charge.requested.$long');
            }
        }

        this.settings.drugApprovedCharges.reqNotMet = false;
        if(this.settings.drugApprovedCharges.reqDataEntry) {
            if(auth.medications && auth.medications.find(m => !m.charge?.approved)) {
                this.settings.drugApprovedCharges.reqNotMet = true;
                missingFields.push('um:medication.charge.approved.$long');
            }
        }

        if(this.settings.drugRequestedDays.reqDataEntry) {
            if(auth.medications && auth.medications.find(m => isNullOrUndefined(m.days?.requested))) {
                this.settings.drugRequestedDays.reqNotMet = true;
                missingFields.push('um:medication.day.requested.$long_plural');
            }
        }

        return missingFields;
    }

    protected checkRequiredForDetermination(auth?: UM.Auth): string[] {
        const missingFields: string[] = [];

        if(!this.isConfigured) throw new Error('Auth context not configured');

        if(!auth) {
            auth = this.auth;
            if(!auth) throw new Error('missing auth object');
        }

        // separate out services from dental services
        const services: UM.AuthService[] = auth?.services?.filter(service => this.filterServicesOnDental(service, false)) || [];
        const dentalServices: UM.AuthService[] = auth?.services?.filter(service => this.filterServicesOnDental(service, true)) || [];

        if(!this.auth.serviceDate?.dateRange?.start && !this.auth.status?.flag?.allowNullDateOfService && !this.allowNullDateOfService) {
            missingFields.push('um:auth.serviceDate.dateRange.start.$long');
        }

        if(!this.auth.serviceDate?.dateRange?.end && !this.auth.status?.flag?.allowNullDateOfService && !this.allowNullDateOfService) {
            missingFields.push('um:auth.serviceDate.dateRange.end.$long');
        }

        if(this.settings.services.reqDet) {
            this.settings.services.reqNotMet = false;

            if(services.length > 0) {

                //check that every service has a determination
                if(!services.every((s) => {
                    if(safe(() => s.status!.determination!.isDetermination)) {
                        return s.status!.determination!.isDetermination;
                    }
                    else { return false; }
                })) {
                    this.settings.servicesStatus.reqNotMet = true;
                    missingFields.push('um:service.status.determination.$long_plural');
                }
            } else {
                this.settings.services.reqNotMet = true;
                missingFields.push('um:service.$_plural');
            }
        }

        if(this.settings.servicesAuthDate.reqDet || this.settings.servicesExpDate.reqDet) {
            let missingField: boolean = false;
            this.settings.servicesAuthDate.reqNotMet = false;

            services.forEach(service => {
                const authorizedDate = service.authorizedDate || null;
                const expirationDate = service.expirationDate || null;
                const serviceValidStart = service.definition?.dateRange?.start || null;
                const serviceValidEnd = service.definition?.dateRange?.end || null;
                let allowNullDateOfService: boolean = service.status?.flag?.allowNullDateOfService || false;

                if(!service.definition?.id) { missingField = true; return; }

                //todo: consider refactoring this out now that we use a status flag to null dates of service (ess-1610)
                if(service.status?.determination?.status?.determination?.result) {
                    //if determination is void, we don't care about dates for that item
                    switch(service.status?.determination?.status?.determination?.result) {
                        case 'denied':
                        case 'other':
                            return;
                    }
                }
                //make sure each field that is required is filled in
                if(!authorizedDate && this.settings.servicesAuthDate.reqDet && !allowNullDateOfService) { missingField = true; return; }
                if(!expirationDate && this.settings.servicesExpDate.reqDet && !allowNullDateOfService) { missingField = true; return; }

                //validate that the dates selected aren't outside the service valid dates
                if(!DateComparison.checkForContainingDates(serviceValidStart, serviceValidEnd,
                    authorizedDate || serviceValidStart, expirationDate || serviceValidEnd)) {
                    missingField = true;
                    return;
                }
            });

            if(missingField) {
                this.settings.servicesAuthDate.reqNotMet = true;
                missingFields.push('um:service.status.date.$_plural');
            }
            // Tim Monh TODO: Add check for overlapping dates if configured to do so. Check history of file to retrieve overlapping date check logic.
        }

        if(this.settings.servicesStatusReason.reqDet) {
            this.settings.services.reqNotMet = false;
            this.settings.servicesStatusReason.reqNotMet = false;

            if(services.length > 0) {

                //check that every service has a reason, provided the selected status has reasons
                if(!services.every((s) => {
                    if(s.status?.reasons?.length) { return !!s.status!.reason; }
                    else { return !!s.status; }
                })) {
                    this.settings.servicesStatusReason.reqNotMet = true;
                    missingFields.push('um:service.status.reason.$long_plural');
                }
            } else {
                this.settings.services.reqNotMet = true;
                missingFields.push('um:service.status.reason.$long_plural');
            }
        }

        if(this.settings.servicesDental.reqDet) {
            this.settings.servicesDental.reqNotMet = false;

            if(dentalServices.length > 0) {

                //check that every dental service has a determination
                if(!dentalServices.every((s) => {
                    if(safe(() => s.status!.determination!.isDetermination)) {
                        return s.status!.determination!.isDetermination;
                    }
                    else { return false; }
                })) {
                    this.settings.servicesDentalStatus.reqNotMet = true;
                    missingFields.push('um:service.status.determination.$long_plural');
                }
            } else {
                this.settings.servicesDental.reqNotMet = true;
                missingFields.push('um:service.dental.$long_plural');
            }
        }

        if(this.settings.servicesDentalAuthDate.reqDet || this.settings.servicesDentalExpDate.reqDet) {
            let missingField: boolean = false;
            this.settings.servicesDentalAuthDate.reqNotMet = false;

            dentalServices.forEach(service => {
                const authorizedDate = service.authorizedDate || null;
                const expirationDate = service.expirationDate || null;
                const serviceValidStart = service.definition?.dateRange?.start || null;
                const serviceValidEnd = service.definition?.dateRange?.end || null;
                let allowNullDateOfService: boolean = service.status?.flag?.allowNullDateOfService || false;

                if(!service.definition?.id) { missingField = true; return; }

                //todo: consider refactoring this out now that we use a status flag to null dates of service (ess-1610)
                if(service.status?.determination?.status?.determination?.result) {
                    //if determination is void, we don't care about dates for that item
                    switch(service.status?.determination?.status?.determination?.result) {
                        case 'other':
                            return;
                    }
                }
                //make sure each field that is required is filled in
                if(!authorizedDate && this.settings.servicesDentalAuthDate.reqDet && !allowNullDateOfService) { missingField = true; return; }
                if(!expirationDate && this.settings.servicesDentalExpDate.reqDet && !allowNullDateOfService) { missingField = true; return; }

                //validate that the dates selected aren't outside the service valid dates
                if(!DateComparison.checkForContainingDates(serviceValidStart, serviceValidEnd,
                    authorizedDate || serviceValidStart, expirationDate || serviceValidEnd)) {
                    missingField = true;
                    return;
                }
            });

            if(missingField) {
                this.settings.servicesDentalAuthDate.reqNotMet = true;
                missingFields.push('um:service.status.date.$_plural');
            }
            // Tim Monh TODO: Add check for overlapping dates if configured to do so. Check history of file to retrieve overlapping date check logic.
        }

        if(this.settings.servicesDentalStatusReason.reqDet) {
            this.settings.servicesDental.reqNotMet = false;
            this.settings.servicesDentalStatusReason.reqNotMet = false;

            if(dentalServices.length > 0) {

                //check that every service has a reason, provided the selected determination has reasons
                if(!dentalServices.every((s) => {
                    if(s.status?.reasons?.length) { return !!s.status!.reason; }
                    else { return !!s.status; }
                })) {
                    this.settings.servicesDentalStatusReason.reqNotMet = true;
                    missingFields.push('um:service.dental.status.reason.$long_plural');
                }
            } else {
                this.settings.servicesDental.reqNotMet = true;
                missingFields.push('um:service.dental.status.reason.$long_plural');
            }
        }

        if(this.settings.chiefComplaint.reqDet) {
            this.settings.chiefComplaint.reqNotMet = !auth.chiefComplaint;
            if(this.settings.chiefComplaint.reqNotMet) {
                missingFields.push('um:auth.chiefComplaint.$');
            }
        }

        if(this.settings.diagnoses.reqDet) {
            if(auth.diagnoses && auth.diagnoses.length > 0) {
                this.settings.diagnoses.reqNotMet = false;
            } else {
                this.settings.diagnoses.reqNotMet = true;
                missingFields.push('um:auth.diagnosis.$_plural');
            }
        }

        if(this.settings.drugs.reqDet) {
            this.settings.drugs.reqNotMet = false;

            if(auth.medications && auth.medications.length > 0) {

                //check that every medication has a determination
                if(!auth.medications.every((m) => {
                    if(safe(() => m.status!.determination!.isDetermination)) {
                        return m.status!.determination!.isDetermination;
                    }
                    else { return false; }
                })) {
                    this.settings.drugsStatus.reqNotMet = true;
                    missingFields.push('cd:drug.determination.$_plural');
                }
            } else {
                this.settings.drugs.reqNotMet = true;
                missingFields.push('cd:drug.$_plural');
            }

        }

        if(this.settings.drugsAuthDate.reqDet || this.settings.drugsExpDate.reqDet) {
            let missingField: boolean = false;
            this.settings.drugsDate.reqNotMet = false;

            this.auth.medications?.forEach(medication => {
                const authorizedDate = medication.authorizedDate || null;
                const expirationDate = medication.expirationDate || null;
                const drugValidStart = medication.definition?.dateRange?.start || null;
                const drugValidEnd = medication.definition?.dateRange?.end || null;
                let allowNullDateOfService: boolean = medication.status?.flag?.allowNullDateOfService || false;

                if(!medication.definition?.id) { missingField = true; return; }

                //todo: consider refactoring this out now that we use a status flag to null dates of service (ess-1610)
                if(medication.status?.determination?.status?.determination?.result) {
                    //if determination is void, we don't care about dates for that item
                    switch(medication.status.determination?.status.determination.result) {
                        case 'other':
                            return;
                    }
                }

                //make sure each field that is required is filled in
                if(!authorizedDate && this.settings.drugsAuthDate.reqDet && !allowNullDateOfService) { missingField = true; return; }
                if(!expirationDate && this.settings.drugsExpDate.reqDet && !allowNullDateOfService) { missingField = true; return; }

                //validate that the dates selected aren't outside the drug valid dates
                if(!DateComparison.checkForContainingDates(drugValidStart, drugValidEnd,
                    authorizedDate || drugValidStart, expirationDate || drugValidEnd)) {
                    missingField = true;
                    return;
                }
            });

            if(missingField) {
                this.settings.drugsDate.reqNotMet = true;
                missingFields.push('um:medication.status.date.$_plural');
            }
            // Tim Monh TODO: Add check for overlapping dates if configured to do so. Check history of file to retrieve overlapping date check logic.
        }

        if(this.settings.drugsStatusReason.reqDet) {
            this.settings.drugs.reqNotMet = false;
            this.settings.drugsStatusReason.reqNotMet = false;

            if(this.auth.medications?.length) {

                //check that every medication has a reason, provided the selected determination has reasons
                if(!this.auth.medications.every((m) => {
                    if(m.status?.reasons?.length) { return !!m.status!.reason; }
                    else { return !!m.status; }
                })) {
                    this.settings.drugsStatusReason.reqNotMet = true;
                    missingFields.push('um:medication.status.reason.$long_plural');
                }
            } else {
                this.settings.drugs.reqNotMet = true;
                missingFields.push('um:medication.status.reason.$long_plural');
            }
        }

        if(this.settings.healthcareServiceType.reqDet) {
            if(!auth.healthcareServiceType) {
                missingFields.push('um:auth.healthcareServiceType.$');
            }
        }

        if(this.settings.placeOfService.reqDet) {
            if(!auth.placeOfService) {
                missingFields.push('cd:auth.placeOfService.$');
            }
        }

        if(this.settings.priority.reqDet) {
            if(!auth.priority) {
                missingFields.push('um:auth.priority.$');
            }
        }

        if(this.settings.providerAttending.reqDet) {
            if(!auth.provider || !auth.provider.attending) {
                missingFields.push(this.settings.providerAttending.display!);
            }
        }

        if(this.settings.providerRequested.reqDet) {
            if(!auth.provider || !auth.provider.requested) {
                missingFields.push(this.settings.providerRequested.display!);
            }
        }

        if(this.settings.providerRequesting.reqDet) {
            if(!auth.provider || !auth.provider.requesting) {
                missingFields.push(this.settings.providerRequesting.display!);
            }
        }

        if(this.settings.providerServicing.reqDet) {
            if(!auth.provider || !auth.provider.servicing) {
                missingFields.push(this.settings.providerServicing.display!);
            }
        }

        if(this.settings.requestedDateTime.reqDet) {
            if(!auth.requestedDateTime) {
                missingFields.push('um:auth.requestedDate.$');
            }
        }

        if(this.settings.admissionSource.reqDet) {
            if(!auth.admission?.source) {
                missingFields.push('um:auth.admission.source.$long');
            }
        }

        if(this.sourceOfAuthReqDet) {
            if(!auth.source) {
                missingFields.push('um:auth.source.$long');
            }
        }

        if(this.transportationReqDet) {
            if(!auth.transportation) {
                missingFields.push('um:auth.transportationAppointment.$long');
            }
        }

        if(this.settings.serviceRequestedCharges.reqDet) {
            if(!services.find(s => !s.charge?.requested)) {
                this.settings.serviceRequestedCharges.reqNotMet = false;
            } else {
                this.settings.serviceRequestedCharges.reqNotMet = true;
                missingFields.push('um:service.charge.requested.$long');
            }
        }

        if(this.settings.serviceApprovedCharges.reqDet) {
            if(!services.find(s => !s.charge?.approved)) {
                this.settings.serviceApprovedCharges.reqNotMet = false;
            } else {
                this.settings.serviceApprovedCharges.reqNotMet = true;
                missingFields.push('um:service.charge.approved.$long');
            }
        }

        if(this.settings.drugRequestedCharges.reqDet) {
            if(!auth.medications || !auth.medications.find(m => !m.charge?.requested)) {
                this.settings.drugRequestedCharges.reqNotMet = false;
            } else {
                this.settings.drugRequestedCharges.reqNotMet = true;
                missingFields.push('um:medication.charge.requested.$long');
            }
        }

        if(this.settings.drugApprovedCharges.reqDet) {
            if(!auth.medications || !auth.medications.find(m => !m.charge?.approved)) {
                this.settings.drugApprovedCharges.reqNotMet = false;
            } else {
                this.settings.drugApprovedCharges.reqNotMet = true;
                missingFields.push('um:medication.charge.approved.$long');
            }
        }

        return missingFields.filter(function(elem, index, self) {
            return index === self.indexOf(elem);
        });

    }

    protected filterServicesOnDental = (service: UM.AuthService, dentalOnly: boolean): boolean => {
        return dentalOnly ? service.definition?.type?.bodySiteType?.code === 'Dental' : service.definition?.type?.bodySiteType?.code !== 'Dental';
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadAuth(authId: string): Promise<UM.Auth> {
        const includes: string[] = [
            'admission.careDays',
            'admission.source',
            'assignedTo',
            'chiefComplaint',
            'class',
            'class.subclass',
            'custom',
            'custom.target',
            'custom.target.definitions',
            'custom.target.definitions.choices',
            'diagnoses',
            'diagnoses.definition.type',
            'discharge.location',
            'externalSystem',
            'healthcareServiceType',
            'placeOfService',
            'preferredContact',
            'medications',
            'medications.definition',
            'medications.status',
            'medications.status.determination.status',
            'medications.status.reasons',
            'metadata.requestedOverride.byUser',
            'metadata.requestedOverride.reason',
            'priority',
            'providers',
            'providers.detail.type',
            'providers.location',
            'providers.type',
            'serviceDate',
            'services',
            'services.bodySite.bodySiteType',
            'services.definition',
            'services.definition.type',
            'services.definition.type.bodySiteType',
            'services.status',
            'services.status.determination.status',
            'services.status.reason',
            'services.status.reasons',
            'source',
            'status',
            'timestamp',
            'type',
            'validation.results'
        ];

        const memberIncludes: string[] = [
            'member',
            'member.address.home',
            'member.gender',
            'member.plan',  //JW: Leaving this here, member-plan-select expects it and we're loading other plan information below
            'member.plan.member',  //CL: Adding this back in to support member-plan-select
            'member.plan.eligibility.history',
            'member.plan.eligibility.history.product',
            'member.plan.healthPlan',
            'member.plan.lineOfBusiness',
            'member.primaryCarePhysician',
            'member.primaryCarePhysician.type'
        ];

        const providerRequestedIncludes: string[] = [
            'provider.requested',
            'provider.requested.location',
            'provider.requested.location.provider',
            'provider.requested.location.facility',
            'provider.requested.location.facility.address.physical'
        ];

        const providerRequestingIncludes: string[] = [
            'provider.requesting',
            'provider.requesting.location',
            'provider.requesting.location.provider',
            'provider.requesting.location.facility',
            'provider.requesting.location.facility.address.physical',
        ];

        const providerServicingIncludes: string[] = [
            'provider.servicing',
            'provider.servicing.location',
            'provider.servicing.location.provider',
            'provider.servicing.location.facility',
            'provider.servicing.location.facility.address.billing',
            'provider.servicing.location.facility.address.physical',
        ];

        const providerAttendingIncludes: string[] = [
            'provider.attending',
            'provider.attending.location',
            'provider.attending.location.provider',
            'provider.attending.location.facility',
            'provider.attending.location.facility.address.physical'
        ];

        const customTargetIncludes: string[] = [
            'definitions',
            'definitions.choices'
        ];


        if(this.syncToClaimsSystem) {
            includes.push('latestEagleTransaction', 'latestEagleTransaction.results');
        }

        // load custom field target if necessary
        const loadCustomFieldTarget = !this.customFieldTarget;

        try {
            const [auth, memberAuth, providerRequestedAuth, providerRequestingAuth, providerServicingAuth, providerAttendingAuth, customTargetAuth] = await Promise.all([
                this.authService.loadAuth(authId, includes),
                this.authService.loadAuth(authId, memberIncludes),
                this.authService.loadAuth(authId, providerRequestedIncludes),
                this.authService.loadAuth(authId, providerRequestingIncludes),
                this.authService.loadAuth(authId, providerServicingIncludes),
                this.authService.loadAuth(authId, providerAttendingIncludes),
                loadCustomFieldTarget ? this.customFieldService.loadCustomFieldTargets({ targetType: 'UM.Auth' }, { top: 1 }, customTargetIncludes) : null
            ]);

            const returnedAuth: UM.Auth = auth.results[0];

            this.latestEagleTransaction = this.syncToClaimsSystem && returnedAuth.latestEagleTransaction ? returnedAuth.latestEagleTransaction : {};
            if(loadCustomFieldTarget) this.customFieldTarget = customTargetAuth?.results[0] || null;

            this.timestamp = auth.timestamp;
            // stitch together the various API calls
            returnedAuth.member = memberAuth.results[0].member;
            if(!returnedAuth.provider) returnedAuth.provider = {};
            returnedAuth.provider.requested = providerRequestedAuth.results[0].provider?.requested;
            returnedAuth.provider.requesting = providerRequestingAuth.results[0].provider?.requesting;
            returnedAuth.provider.servicing = providerServicingAuth.results[0].provider?.servicing;
            returnedAuth.provider.attending = providerAttendingAuth.results[0].provider?.attending;
            if(!returnedAuth.custom) returnedAuth.custom = {}
            returnedAuth.custom.target = this.customFieldTarget;

            return returnedAuth;
        }
        catch(error) {
            throw error;
        }
        finally {
        }
    }

    @apiCall
    private async dataEntryComplete(authId: string): Promise<void> {
        try {
            const response = await this.authService.completeEntry({ id: authId });
            if(!response.isSuccessful) {
                this.toaster.error().message('Unable to move into data entry complete status').show();
            }
        }
        catch(error) {
            throw error;
        }
        finally {
        }
    }
}
